import { useMemo } from 'react';
import { CallBadge } from './CallBadge';
import { reasoningTooltip } from './ReasoningBadge';
import {
  partBoxPrompt, redoPartBoxes, redoPictures, refineBoxesPrompt, usePartsRun, type PartItem,
} from './partsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// WHERE EACH PART IS DRAWN IN THE MANUAL: finding the boxes again, the question, and what the
// last search said. Beside the phase button with the other calls — it was the SCHEMA row's
// Redo and its badge, in the table's label column.
//
// WHICHEVER OF THE TWO CALLS THE SCOPE MAKES. One part open is one full page per page of the
// manual and one box back; "All parts" is the page with every box drawn and tagged and the
// whole corrected set back. A badge showing the other one would describe a call this press
// will not make.
export function SchemaBoxesInfo({ docId, file, items, only, disabled, onStop }: {
  docId: string;
  file: Blob;
  // The parts this describes — see PartsPhase, which keeps the last list this document had.
  items: PartItem[];
  // The open part, or undefined for every part.
  only?: number;
  // Something else is in flight — the whole press — or the phase is locked. See CallRun.
  disabled: boolean;
  // STOP, while this badge's run is out and was started on its own (see CallRun.onStop).
  onStop?: () => void;
}) {
  const { t } = useLanguage();
  const { redo: notes, reboxing } = usePartsRun(docId);
  const one = only != null ? items[only] : undefined;

  // ONE SECTION PER CALL, headed by the page it was about — or by the part, where the search
  // was over the whole manual and the page is what came back. Written by both forms and by
  // the automatic pass at the end of every extraction.
  const answer = useMemo(() => {
    if (reboxing !== null || !notes.length) return undefined;
    const name = (i: number) => items[i]?.nameEn || items[i]?.name || t('notice.parts.piece');
    return {
      label: t('notice.cat.thinking'),
      content: (
        <div className="space-y-4">
          {notes.map((n, at) => (
            <div key={at}>
              <div className="mb-1 font-semibold">
                {n.part != null ? name(n.part) : `${t('notice.pdf.page')} ${n.page ?? '—'}`}
                {n.part != null && n.page != null && ` · ${t('notice.pdf.page')} ${n.page}`}
              </div>
              {reasoningTooltip(n.reasoning, n.raw, t)}
            </div>
          ))}
        </div>
      ),
    };
  }, [notes, reboxing, items, t]);

  // DRAWN EVEN WITH NOTHING TO ASK ABOUT, greyed out: the phase shows its whole sequence.
  const nothingToAsk = !items.length;

  return (
    <CallBadge
      title={t('notice.parts.badgeSchema')}
      named
      run={{
        onRun: () => void redoPartBoxes(docId, file, only, t),
        busy: reboxing !== null,
        disabled: disabled || nothingToAsk,
        // ONE PART is one call, already out: nothing a Stop could hold back.
        onStop: only == null ? onStop : undefined,
      }}
      promptLabel={t('notice.parts.redoPrompt')}
      load={() => (one ? partBoxPrompt(one) : refineBoxesPrompt(items))}
      more={() => redoPictures(file, items, only, t)}
      // Refetched when the scope changes: the two forms are different questions.
      revision={only == null ? 'all' : String(only)}
      answer={answer}
    />
  );
}
